import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import CreateIcon from '@mui/icons-material/Create';
import orderBy from 'lodash/fp/orderBy';

import { ILabels, ISkill, JobTalent } from '@bloomays-lib/types.shared';
import { constructDateDetails } from '@bloomays-lib/utils.shared';

import TalentSectionContainer from './TalentSectionContainer';
import CardItem from '../atoms/CardItem';
import SingleLineText, { SingleLineTextProps } from '../atoms/SingleLineText';
import ItemTalentLevelList from '../molecules/ItemTalentLevelList';

export type TalentExperiencesSectionProps = {
  jobs: JobTalent[];
  addActionFn?: () => void;
  setOpenXP?: (openXP: { item: JobTalent; show: boolean }) => void;
};

const ExperienceDetails = ({
  job,
  isCollapsed,
  isCollapsible,
  setExpandedJobId,
  expandedJobId,
  isExpanded,
}: {
  job: JobTalent;
  isCollapsed: boolean;
  isCollapsible: boolean;
  setExpandedJobId: (id: string | null) => void;
  expandedJobId: string | null;
  isExpanded: boolean;
}) => {
  const { t } = useTranslation(['random']);
  const hasDescription = typeof job?.description === 'string' && job.description.length > 0;
  const hasSkills = Array.isArray(job?.skills) && job.skills?.length > 0;

  const skills = job.skills?.map((skill: ILabels) => ({
    skill: {
      id: skill.id,
      label: skill.label,
      iso: skill.iso,
    },
  }));

  if (!isCollapsible) {
    return (
      <>
        {hasDescription && <JobDescription dangerouslySetInnerHTML={{ __html: job?.description || '' }} />}
        {hasSkills && <ItemTalentLevelList items={skills} />}
      </>
    ) as React.ReactNode;
  }

  return (
    <>
      <CollapsibleDetails $isCollapsed={isCollapsed}>
        {hasDescription && <JobDescription dangerouslySetInnerHTML={{ __html: job?.description || '' }} />}
        {hasSkills && <ItemTalentLevelList items={skills} forceShowAll />}
      </CollapsibleDetails>
      <ToggleButton
        type="button"
        onClick={() => {
          setExpandedJobId(expandedJobId === job.id ? null : (job.id as string | null));
        }}
      >
        <SingleLineText text={isExpanded ? t('seeLess', { ns: 'random' }) : t('seeMore', { ns: 'random' })} />
      </ToggleButton>
    </>
  ) as React.ReactNode;
};

const TalentExperiencesSection = ({ jobs, addActionFn, setOpenXP }: TalentExperiencesSectionProps) => {
  const { t } = useTranslation(['talentSheet', 'random']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);

  return (
    <TalentSectionContainer
      title={t('jobs')}
      emptyText={t('noXp')}
      addActionFn={addActionFn}
      addActionText={t('addXP', { ns: 'talentSheet' })}
    >
      {jobs && jobs.length > 0 && (
        <ContentWrapper>
          {orderBy(
            (item: JobTalent) => item?.period?.startDate,
            ['desc'],
          )(jobs)?.map((job, i) => {
            const isExpanded = expandedJobId === job.id;
            const hasCollapsibleDescription = typeof job?.description === 'string' && job.description.length > 500;
            const hasCollapsibleSkills = Array.isArray(job?.skills) && job.skills?.length > 20;
            const isCollapsible = isMobile && (hasCollapsibleDescription || hasCollapsibleSkills);
            const isCollapsed = isCollapsible && !isExpanded;

            return (
              <Experience key={job.id}>
                {setOpenXP && (
                  <StyledRowContainerAdd
                    onClick={() => {
                      setOpenXP({
                        item: job,
                        show: true,
                      });
                    }}
                  >
                    <CreateIcon />
                  </StyledRowContainerAdd>
                )}
                <StyledContainerXP>
                  {!isMobile && (
                    <CardItem
                      variant={'blue'}
                      size={'medium'}
                      emoji={<StyledSpan>{job?.name?.substring(0, 1)}</StyledSpan>}
                    />
                  )}
                  <StyledContainerXPDetails>
                    <StyledContainerJobTitle>
                      {job?.jobTitle &&
                        job?.jobTitle?.map((j, i) => {
                          return (
                            <div key={`${j.label}-${i}`}>
                              <StyledTitles variant={'heading6'} text={j.label} />
                              {i < (job?.jobTitle?.length as number) - 1 && '/'}
                            </div>
                          );
                        })}
                    </StyledContainerJobTitle>
                    <StyledTitles variant={'subtitle2Medium'} text={job?.name} />
                    <StyledDateText variant={'subtitle1'} text={constructDateDetails(job.period, job?.current)} />
                    <ExperienceDetails
                      job={job}
                      isCollapsed={isCollapsed}
                      isCollapsible={isCollapsible}
                      setExpandedJobId={setExpandedJobId}
                      expandedJobId={expandedJobId}
                      isExpanded={isExpanded}
                    />
                  </StyledContainerXPDetails>
                </StyledContainerXP>
                {jobs && jobs?.length - 1 !== i && <StyledBarXP></StyledBarXP>}
              </Experience>
            );
          })}
        </ContentWrapper>
      )}
    </TalentSectionContainer>
  ) as React.ReactNode;
};

export { TalentExperiencesSection };
export default TalentExperiencesSection;

const ContentWrapper = styled('div')(() => ({
  display: 'flex',
  gap: '16px',
  flexDirection: 'column',
}));

const Experience = styled('div')(
  () => `
  position: relative;
  padding: 12px 0;
  `,
);

const StyledRowContainerAdd = styled('div')(
  ({ theme }) => `
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    justify-content: flex-end;
    text-decoration: underline;
    position: absolute;
    right: 0;
    color: ${theme.palette.neutral[80]};
    &:hover {
      cursor: pointer;
    }
    `,
);

const StyledContainerXP = styled('div')(
  () => `
    display: flex;
    flex-flow: row nowrap;
    gap: 24px;
    align-items: flex-start;
    `,
);

const StyledTitles: React.FC<SingleLineTextProps> = styled(SingleLineText)(
  ({ theme }) => `
    color: ${theme.palette.neutral[80]};
    `,
);

const StyledContainerXPDetails = styled('div')(
  () => `
    display: flex;
    flex-flow: column nowrap;
    text-align: left;
    max-width: 1280px;
    gap: 8px;
    overflow: hidden;
    `,
);

const StyledContainerJobTitle = styled('div')(
  () => `
    display: flex;
    flex-flow: row wrap;
    gap: 4px;
    align-items: flex-start;
    `,
);

const StyledDateText: React.FC<SingleLineTextProps> = styled(SingleLineText)(({ theme }) => ({
  color: theme.palette.neutral[30],

  [theme.breakpoints.down('md')]: {
    fontSize: '11px',
    lineHeight: '10px',
  },
}));

const JobDescription = styled('div')(({ theme }) => ({
  fontSize: '13px',
  color: theme.palette.neutral[80],

  [theme.breakpoints.down('md')]: {
    fontSize: '11px',
    marginBottom: '12px',
  },
}));

const StyledBarXP = styled('div')(
  ({ theme }) => `
    height: 0px;
    margin-top: 32px;
    border: 1px solid ${theme.palette.neutral[20]};
    `,
);

const StyledSpan = styled('span')(
  () => `
  font-size: 16px;
  `,
);

const CollapsibleDetails = styled('div')<{ $isCollapsed: boolean }>(({ $isCollapsed }) => ({
  maxHeight: $isCollapsed ? '60px' : 'none',
  overflow: $isCollapsed ? 'hidden' : 'visible',
}));

const ToggleButton = styled('button')(({ theme }) => ({
  marginTop: theme.spacing(1),
  background: theme.palette.neutral[20],
  border: 'none',
  borderRadius: '24px',
  padding: 0,
  cursor: 'pointer',
  height: '36px',

  '& p': {
    fontSize: '13px',
  },
}));
